import { createAsyncThunk } from '@reduxjs/toolkit';
import { setColumnFilters } from './searchAndFilterSlice';
import { ColumnFilterMap, FilterOption } from 'util/filtering/models';
import { RootState } from 'app/store/rootReducer';
import {
  getUsers,
  clearUsers,
  getAllUsersNoLimit,
  DEFAULT_USERS_PAGE_SIZE,
  resetNextToken,
} from 'app/pages/store/usersSlice';
import {
  generateUserFilterObject,
  buildColumnFiltersFromConfig,
  DynamicFilterOptions,
} from './userFilterConfig';
import { PaidolUser } from 'API';
import { searchFlexCodesDebounced, searchFlexGlCodesDebounced } from 'app/pages/store/flexCodingSlice';
import { hasErpCodingUI } from 'app/util/paidolAccountType';

export const retrieveUsersManagementFilterOptions = createAsyncThunk(
  'searchAndFilter/usersManagement/retrieveFilterOptions',
  async ({ pageName, paidolId }: { pageName: string; paidolId: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const selectedPaidol = state.userCompanies.selectedPaidol;

    // Preload Flex project and GL code options for filters (Agave/ErpConnection load on dropdown open)
    if (!hasErpCodingUI(selectedPaidol)) {
      dispatch(searchFlexCodesDebounced({ paidolId, glCodeId: '', searchTerm: '', isSettingsRequest: true }));
      dispatch(searchFlexGlCodesDebounced({ paidolId, searchTerm: '', isSettingsRequest: true }));
    }

    const usersResult = await dispatch(getAllUsersNoLimit({ selectedCompany: paidolId }));
    const allUsers = (
      usersResult.payload && Array.isArray(usersResult.payload) ? usersResult.payload : []
    ) as Array<PaidolUser>;

    const nameSet = new Set<string>();
    const emailSet = new Set<string>();
    const usernameSet = new Set<string>();

    allUsers.forEach((user: PaidolUser) => {
      if (user.deletedAt) {
        return;
      }

      if (user.user?.first_name || user.user?.last_name) {
        const fullName = `${user.user.first_name || ''} ${user.user.last_name || ''}`.trim();
        if (fullName) {
          nameSet.add(fullName);
        }
      }

      const email = user.user?.email || user.email;
      if (email) {
        emailSet.add(email);
      }

      if (user.user?.username) {
        usernameSet.add(user.user.username);
      }
    });

    const dynamicOptions: DynamicFilterOptions = {
      name: Array.from(nameSet)
        .sort()
        .map((name): FilterOption => ({ id: name, label: name })),
      email: Array.from(emailSet)
        .sort()
        .map((email): FilterOption => ({ id: email, label: email })),
      username: Array.from(usernameSet)
        .sort()
        .map((username): FilterOption => ({ id: username, label: username })),
      // projectVisibility and glCodeVisibility will be populated via search
      projectVisibility: [],
      glCodeVisibility: [],
    };

    const columnFilters = buildColumnFiltersFromConfig(pageName, dynamicOptions);

    // Get existing filters to preserve any selected values
    const searchAndFilter = state.searchAndFilter[pageName];
    const existingFilters = searchAndFilter?.columnFilters || {};

    // Merge new options with existing filters, preserving selectedValues
    const mergedFilters = Object.keys(columnFilters).reduce((acc, key) => {
      const newFilter = columnFilters[key];
      const existingFilter = existingFilters[key];

      acc[key] = {
        ...newFilter,
        // Preserve existing selectedValues if they exist
        selectedValues: existingFilter?.selectedValues || newFilter.selectedValues,
        active: existingFilter?.active ?? newFilter.active,
        rule: existingFilter?.rule || newFilter.rule,
      };
      return acc;
    }, {} as ColumnFilterMap);

    dispatch(
      setColumnFilters({
        pageName,
        columnFilters: mergedFilters,
      })
    );

    return { pageName };
  }
);

export const applyUsersColumnFilters = createAsyncThunk(
  'searchAndFilter/usersManagement/applyColumnFilters',
  async (
    { pageName, columnFilters }: { pageName: string; columnFilters: ColumnFilterMap },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    dispatch(setColumnFilters({ pageName, columnFilters }));

    const filter = generateUserFilterObject(columnFilters);

    const searchAndFilter = state.searchAndFilter[pageName];
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    dispatch(clearUsers());

    dispatch(
      getUsers({
        rowsPerPage: DEFAULT_USERS_PAGE_SIZE,
        nextToken: null,
        selectedCompany: paidolId,
        prioritizeCurrentUser: false,
        filter,
        sort,
      })
    );
  }
);

export const applyUsersSortFilter = createAsyncThunk(
  'searchAndFilter/usersManagement/applySortFilter',
  async (
    { pageName, sortOption }: { pageName: string; sortOption: { field: string; direction: string } },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    const columnFilters = state.searchAndFilter[pageName]?.columnFilters || {};
    const sort = sortOption?.field ? sortOption : undefined;

    const filter = generateUserFilterObject(columnFilters);

    dispatch(clearUsers());

    dispatch(
      getUsers({
        rowsPerPage: DEFAULT_USERS_PAGE_SIZE,
        nextToken: null,
        selectedCompany: paidolId,
        prioritizeCurrentUser: false,
        filter,
        sort,
      })
    );
  }
);

export const executeUsersLoadMore = createAsyncThunk(
  'searchAndFilter/usersManagement/executeLoadMore',
  async ({ pageName, nextToken }: { pageName: string; nextToken?: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    const searchAndFilter = state.searchAndFilter[pageName];
    const columnFilters = searchAndFilter?.columnFilters || {};
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    const filter = generateUserFilterObject(columnFilters);

    if (!nextToken) {
      dispatch(clearUsers());
    }

    dispatch(
      getUsers({
        rowsPerPage: DEFAULT_USERS_PAGE_SIZE,
        nextToken: nextToken || null,
        selectedCompany: paidolId,
        prioritizeCurrentUser: false,
        filter,
        sort,
      })
    );
  }
);

export const executeUsersSearch = createAsyncThunk(
  'searchAndFilter/usersManagement/executeSearch',
  async (pageName: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    const searchAndFilter = state.searchAndFilter[pageName];
    const columnFilters = searchAndFilter?.columnFilters || {};
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    const filter = generateUserFilterObject(columnFilters);

    dispatch(clearUsers());

    dispatch(
      getUsers({
        rowsPerPage: DEFAULT_USERS_PAGE_SIZE,
        nextToken: null,
        selectedCompany: paidolId,
        prioritizeCurrentUser: false,
        filter,
        sort,
      })
    );
  }
);

export const clearUsersColumnFilters = createAsyncThunk(
  'searchAndFilter/usersManagement/clearColumnFilters',
  async ({ pageName }: { pageName: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    dispatch(clearUsers());
    dispatch(resetNextToken());
    dispatch(
      getUsers({
        rowsPerPage: DEFAULT_USERS_PAGE_SIZE,
        nextToken: null,
        selectedCompany: paidolId,
        prioritizeCurrentUser: true,
      })
    );
  }
);
